import React, { useEffect, useRef } from 'react'
import { useField, useFormikContext } from 'formik'
import { TextField, CircularProgress, styled } from '@mui/material'
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api'
import classNames from 'classnames'
import styles from './GooglePlacesAutocomplete.module.scss'
import { Location } from '../../../containers/BookableActivityPage/interfaces'

const StyledAutocomplete = styled(Autocomplete)`
  & .MuiInputBase-root {
    display: flex;
    align-items: center;
    color: black;
    height: 38px;
  }
  & .MuiOutlinedInput-notchedOutline {
    border-radius: 10px;
    color: black;
  }
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 1px gray solid;
    box-shadow: 0 0 0 4px white;
  }
`

interface GooglePlacesAutocompleteProps {
  name: string
  label: string
  location: Location[]
}
type Library =
  | 'core'
  | 'maps'
  | 'places'
  | 'geocoding'
  | 'routes'
  | 'marker'
  | 'geometry'
  | 'elevation'
  | 'streetView'
  | 'journeySharing'
  | 'drawing'
  | 'visualization'

const libraries: Library[] = ['places']
const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  name,
  label,
  location,
}) => {
  const { setFieldValue } = useFormikContext()
  const [field, meta] = useField(name)
  const inputClasses = classNames(styles.input)
  const inputFieldClasses = classNames(styles.field, styles.test)
  const [autocomplete, setAutocomplete] = React.useState<google.maps.places.Autocomplete | null>(
    null,
  )
  const [loading, setLoading] = React.useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
    libraries: libraries,
  })

  useEffect(() => {
    if (location && location.length > 0 && location[0]?.address) {
      setFieldValue(name, location[0].address)
    }
  }, [location, setFieldValue, name])

  const onLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance)
  }

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace()
      if (place.formatted_address) {
        setFieldValue(name, place.formatted_address)
      }
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        setFieldValue(`location`, {
          latitude: lat,
          longitude: lng,
          google_place_id: place.place_id,
          address: place.formatted_address,
        })
      }
    }
  }

  const handleFocus = () => {
    const pacContainer = document.querySelector('.pac-container')
    const parent = inputRef.current?.parentNode
    if (pacContainer && parent) {
      parent.appendChild(pacContainer)
    }
  }

  return (
    <div className={inputFieldClasses} style={{ width: '100%', position: 'relative' }}>
      {isLoaded ? (
        <StyledAutocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          className={inputClasses}
        >
          <TextField
            {...field}
            value={
              typeof field.value === 'object' && field.value !== null
                ? field.value.address
                : field.value || ''
            }
            onFocus={handleFocus}
            ref={inputRef}
            label={label}
            variant="outlined"
            fullWidth
            InputProps={{
              endAdornment: loading ? <CircularProgress size={20} /> : null,
            }}
            onChange={(event) => {
              setLoading(true)
              field.onChange(event)
              setLoading(false)
            }}
          />
        </StyledAutocomplete>
      ) : (
        <CircularProgress />
      )}
    </div>
  )
}

export default GooglePlacesAutocomplete
